import {createSlice} from "@reduxjs/toolkit";
import api from "../helpers/api";
import _ from "lodash";
import {$t} from "../helpers/translator";

const dataSlice = createSlice({
    name: 'data',
    initialState: {
        countries: null,
        errorTypes: null,
        areaTypes: null,
        clinics: null,
        applicators: null,
        platforms: null,

        rolesAndPermissions: null,
        permissions: []
    },
    reducers: {
        setCountries(state, action) {
            state.countries = action.payload;
        },

        setPlatforms(state, action) {
            state.platforms = action.payload;
        },

        setApplicators(state, action) {
            state.applicators = action.payload;
        },

        setClinics(state, action) {
            state.clinics = action.payload;
        },

        setErrorTypes(state, action) {
            state.errorTypes = action.payload;
        },

        setAreaTypes(state, action) {
            state.areaTypes = action.payload;
        },

        setRolesAndPermissions(state, action) {
            state.rolesAndPermissions = action.payload;
        },

        setAllPermissions(state, action) {
            state.permissions = action.payload;
        },

        clearData(state) {
            state.clinics = null;
            state.applicators = null;
            state.platforms = null;
            state.rolesAndPermissions = null;
        }
    }
});

export const getCountries = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.countries && !force) {
            return state.countries;
        }

        try {
            let res = await api.get('/general/countries');
            let countries = [];
            if (res && res.success && res.data && res.data.data) {
                _.forEach(res.data.data, (v, k) => {
                    countries.push({
                        ...v,
                        label: v.name,
                        value: v.id,
                    })
                })
                dispatch(dataActions.setCountries(countries))
            }
            return countries;
        } catch (e) {
            console.error('error', e)
        }
        return state.countries;
    }
}

export const getApplicators = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.applicators && !force) {
            return state.applicators;
        }

        try {
            let res = await api.get('general/applicators');
            let applicators = [];
            if (res.success) {
                _.forEach(res.data.data, (v, k) => {
                    applicators.push({
                        ...v,
                        label: `${v.handle_id}${(v.serial_number ? ' (' + v.serial_number + ')' : '')}`,
                        value: v.id,
                    })
                })
                dispatch(dataActions.setApplicators(applicators))
            }
            return applicators;
        } catch (e) {
            console.error('error', e)
        }
        return state.applicators;
    }
}

export const getPlatforms = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.platforms && !force) {
            return state.platforms;
        }

        try {
            let res = await api.get('general/platforms');
            let platforms = [];
            let orderedPlatforms = [];

            if (res.success) {
                _.forEach(res.data.data, (v, k) => {
                    platforms.push({
                        ...v,
                        label: `${v.machine_id} (${v.machine_name})`,
                        value: v.id,
                    })
                })
                orderedPlatforms = _.orderBy(platforms, ['label'])
                dispatch(dataActions.setPlatforms(orderedPlatforms))
            }
            return orderedPlatforms;
        } catch (e) {
            console.error('error', e)
        }
        return state.platforms;
    }
}

export const getClinics = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.clinics && !force) {
            return state.clinics;
        }

        try {
            let clinics = [];
            let orderedClinics = []
            let res = await api.get('general/clinics');
            if (res.success) {
                _.forEach(res.data.data, (v, k) => {
                    let clinic = {
                        ...v,
                        label: v.name,
                        value: v.id,
                    }
                    clinics.push(clinic)
                })

                // console.log(clinics)
                orderedClinics = _.orderBy(clinics, ['label'])
                orderedClinics = _.filter(orderedClinics, function (o) {
                    return o.label;
                });
                // console.log(orderedClinics)

                dispatch(dataActions.setClinics(orderedClinics))
            }

            return orderedClinics;
        } catch (e) {
            console.error('error', e)
        }
        return state.clinics;
    }
}

export const getErrorTypes = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.errorTypes && !force) {
            return state.errorTypes;
        }

        try {
            let errorTypes = [];
            let res = await api.get('/general/errors');
            if (res.success) {
                _.forEach(res.data.data, (v, k) => {
                    errorTypes.push({
                        ...v,
                        value: v.id,
                        label: $t('global.errorTypes.' + v.title),
                    });
                });
                dispatch(dataActions.setErrorTypes(errorTypes))
            }
            return errorTypes;
        } catch (e) {
            console.error('error', e)
        }
        return state.errorTypes;
    }
}

export const getAreaTypes = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.areaTypes && !force) {
            return state.areaTypes;
        }

        try {
            let areas = [];
            let res = await api.get('general/areas');
            if (res.success) {
                _.forEach(res.data.data, (v, k) => {
                    areas.push({
                        ...v,
                        value: v.id,
                        label: $t('global.areas.' + v.name),
                    });
                });
                dispatch(dataActions.setAreaTypes(areas))
            }
            return areas;
        } catch (e) {
            console.error('error', e)
        }
        return state.areaTypes;
    }
}

export const getAllPermissions = (force = false) => {
    return async (dispatch, getState) => {
        let state = getState().data;
        if (state.permissions && !force) {
            return state.permissions;
        }

        try {
            let res = await api.get('permissions');
            if (res.success) {
                dispatch(dataActions.setAllPermissions(res.data))
            }
            return res.data;
        } catch (e) {
            console.error('error', e)
        }
        return state.permissions;
    }
}

export const getRolesAndPermissions = (force = false) => {
    return async (dispatch, getState) => {
        //checking if account data already exists. if exists and force is false, no need to get data from server;
        let state = getState().data;
        if (state.rolesAndPermissions && !force) {
            // console.log(state.rolesAndPermissions, !force);
            return state.rolesAndPermissions;
        }

        try {
            let roles = {};
            let res = await api.get('permissions/roles');
            // console.log('getSubUserRoles >>>', res);
            if (res.success) {
                _.forEach(res.data, (role, roleName) => {
                    if (!roles[role.id]) {
                        roles[role.id] = {};
                    }

                    _.forEach(role.subRoles, (v, subRoleName) => {
                        roles[role.id][subRoleName] = {
                            ...v,
                            value: v.name,
                            label: $t('global.account.roles.' + v.name + '.name'),
                            description: $t('global.account.roles.' + v.name + '.description'),
                        };
                    });
                })

                dispatch(dataActions.setRolesAndPermissions(roles))
            }
            return roles;
        } catch (e) {
            console.error('error', e)
        }
        return state.rolesAndPermissions;
    }
}

export const getPermissionsByRole = (role = null, force = false) => {
    return async (dispatch, getState) => {
        let state = getState().data;
        await getRolesAndPermissions(force);

        if (!role) {
            return state.rolesAndPermissions;
        }

        if (!state.rolesAndPermissions[role]) {
            return {};
        }

        return state.rolesAndPermissions[role];
    }
}

export const clearData = () => {
    return async (dispatch) => {
        dispatch(dataActions.clearData());
    }
}

export const dataActions = dataSlice.actions;
export default dataSlice.reducer;
